import { useCallback } from "react";
import React from "react";
import { graphql, useFragment } from "react-relay";

import { faPlus, faTrashCan } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";

import { RadioButton } from "~/feedback/FormInputs";
import { Button, Checkbox } from "~/ui";
import { IconButton } from "~/ui/legacyFellowIndex";

import CopilotSettingsUserFilter from "./CopilotSettingsUserFilter";
import { useCreateAndEditModal } from "./CreateAndEdit";

import type { UserSelectionStepGetIncludesString$key } from "~/__generated__/UserSelectionStepGetIncludesString.graphql";

import styles from "./UserSelectionStep.module.css";

function IncludeAllReportsCheckbox({ index, disabled }: { index: number; disabled?: boolean }) {
	const context = useCreateAndEditModal();

	const autoRecordRuleUser = context.users[index];

	const onClick = useCallback(() => {
		if (!autoRecordRuleUser) {
			return;
		}
		context.updateUserInput(index, {
			user: autoRecordRuleUser.user,
			includeAllReports: !autoRecordRuleUser.includeAllReports,
		});
	}, [autoRecordRuleUser, context, index]);

	if (!autoRecordRuleUser) {
		return (
			<span className={styles.includeAllReports}>
				<Checkbox disabled={true} checked={true} onClick={onClick} />
				<span className={styles.disabled}>Include all reports</span>
			</span>
		);
	}

	return (
		<span className={cx(styles.includeAllReports, { [styles.disabled]: disabled })}>
			<Checkbox disabled={disabled} checked={autoRecordRuleUser.includeAllReports} onClick={onClick} />
			<span className={disabled ? styles.disabled : undefined}>Include all reports</span>
		</span>
	);
}

function UserSelector({ index }: { index: number }) {
	const context = useCreateAndEditModal();

	const selectedUserInput = context.users[index];

	const onSelectUser = useCallback(
		user => {
			context.updateUserInput(index, {
				user: user,
				includeAllReports: true,
			});
		},
		[context, index],
	);

	return (
		<div className={styles.userOrWorkspaceSelector}>
			<CopilotSettingsUserFilter
				searchParam={""}
				triggerText="Select a user"
				value={selectedUserInput?.user || null}
				onSelectUser={onSelectUser}
			/>
		</div>
	);
}

export function useGetIncludesString(
	userRef: { user: UserSelectionStepGetIncludesString$key | null; includeAllReports: boolean } | null,
) {
	const getIncludesStringFragment = graphql`
		fragment UserSelectionStepGetIncludesString on User {
			fullName
			internalAttributes {
				allReports {
					edges {
						node {
							fullName
						}
					}
				}
			}
		}
	`;

	const user = useFragment<UserSelectionStepGetIncludesString$key>(getIncludesStringFragment, userRef?.user || null);

	if (!userRef?.includeAllReports) {
		return undefined;
	}

	const names = user?.internalAttributes?.allReports?.edges.map(
		(edge: { node: { fullName: string } }) => edge.node.fullName,
	);

	if (!user) {
		return undefined;
	}

	let includesString = undefined;
	if (names) {
		const first25Names = names.slice(0, 25);
		const remainingNames = names.slice(25);
		includesString =
			first25Names.join(", ") + (remainingNames.length > 0 ? ` and ${remainingNames.length} others` : "");
	}

	return includesString ? `Includes: ${includesString}` : `${user.fullName} currently has no reports`;
}

function UserSelectionOptions({ index }: { index: number }) {
	const context = useCreateAndEditModal();
	const selectedUser = context.users[index];

	let includesString = useGetIncludesString(selectedUser);

	const onRemove = useCallback(() => {
		context.removeUserInput(index);
	}, [context, index]);

	return (
		<div className={styles.userSelectionContainer}>
			<div className={styles.userSelectionOptions}>
				<div className={styles.userSelectionOptionsRow}>
					<div className={styles.userSelectionRow}>
						<div className={styles.userSelectionOptionsRow}>
							<UserSelector index={index} />
							<IncludeAllReportsCheckbox index={index} />
						</div>
						{index !== 0 && (
							<span>
								<IconButton icon={faTrashCan} onClick={onRemove} iconSize="sm" className={styles.deleteIcon} />
							</span>
						)}
					</div>
				</div>
				{includesString && <div className={cx(styles.row, styles.reportsList)}>{includesString}</div>}
			</div>
		</div>
	);
}

export function UserSelectionStep() {
	const context = useCreateAndEditModal();

	const onAddUser = useCallback(() => {
		context.addUserInput(null);
	}, [context]);

	const onSelectUsers = useCallback(() => {
		// Prevent adding an empty user selection when clicking on the "Select users" button multiple times
		if (context.ruleLevel == "GROUP") {
			return;
		}

		context.setRuleLevel("GROUP");
		context.addUserInput(null);
	}, [context]);

	const onSelectWorkspace = useCallback(() => {
		context.setRuleLevel("WORKSPACE");
		context.setUsers([]);
	}, [context]);

	const isWorkspaceRule = context.ruleLevel === "WORKSPACE";
	const isGroupRule = context.ruleLevel === "GROUP";

	return (
		<>
			<p className={styles.userSelectionDescription}>Who will the policy apply to?</p>
			<div className={styles.radioButtonContainer}>
				<div className={styles.radioButtonRow} onClick={onSelectWorkspace}>
					<RadioButton checked={isWorkspaceRule} />
					Entire workspace
				</div>
			</div>
			<div className={styles.radioButtonContainer}>
				<div className={styles.radioButtonRow} onClick={onSelectUsers}>
					<RadioButton checked={isGroupRule} />
					Select users
				</div>
				{isGroupRule && (
					<div className={styles.userSelectionColumn}>
						{context.users.map((user, index) => {
							return (
								// eslint-disable-next-line react/no-array-index-key
								<UserSelectionOptions index={index} key={`${user?.user.id} + ${index}`} />
							);
						})}
						<Button variant="placeholder" onClick={onAddUser}>
							<div className={styles.addUserButtonContent}>
								<FontAwesomeIcon icon={faPlus} />
								Add user(s)
							</div>
						</Button>
						<span className={styles.muted}>Pro tip: Multiple policies can be applied to the same user</span>
					</div>
				)}
			</div>
		</>
	);
}
